import React from 'react';
import {Card, Tooltip, Typography} from 'antd';
import styles from './style.module.css';
import {InfoCircleOutlined} from '@ant-design/icons';
import {formatValue} from '../../../../utils/helpers';

const {Title} = Typography;

/**
 * A reusable statistic card component for displaying key metrics, with optional tooltip information.
 *
 * @param {Object} props - The properties passed to the statistic card component.
 * @param {string} props.title - The title displayed at the top of the card, describing the metric.
 * @param {number} props.value - The value of the statistic to display.
 * @param {string} [props.symbol] - An optional symbol to display alongside the value (e.g., currency or units).
 * @param {string} [props.info] - Optional tooltip information providing additional details about the statistic.
 * @param {string} [props.className] - Optional custom CSS class for styling the card.
 *
 * @returns {React.ReactElement} A styled card displaying a title, statistic value, and optional tooltip.
 */
type CardProps = {
    title: string;
    value: number;
    symbol?: string;
    info?: string;
    className?: string;
};

const StatisticCard: React.FC<CardProps> = ({title, value, symbol, info, className}) => {
    let text = value.toString();
    if (symbol) text = symbol + ' ' + formatValue(value);

    return (
        <Card className={className ? `${styles[className]}` : undefined}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <Title level={5} style={{margin: 0}}>{title}</Title>
                {info && (
                    <Tooltip title={info} style={{backgroundColor: 'rgba(126, 212, 236, 0.8)'}}>
                        <InfoCircleOutlined/>
                    </Tooltip>
                )}
            </div>
            <Title level={2} style={{margin: '10px 0', color: 'rgba(126, 212, 236, 0.8)'}}>{text}</Title>
        </Card>
    );
};

export default StatisticCard;